import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/ui/LanguageSwitcher';
import NotificationBell from './NotificationBell';
import FeedbackWidget from '@/components/feedback/FeedbackWidget';
import { initials, useAuth } from '@/lib/auth';
import { findPortalNavKey, mobileTabPaths, visiblePortalNav, type PortalNavItem } from './portalNav';

function NavRow({ item, onNavigate }: { item: PortalNavItem; onNavigate?: () => void }) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={item.path}
      end={item.path === '/portal'}
      onClick={onNavigate}
      className={({ isActive }) =>
        `group relative flex items-center gap-2.5 rounded-md px-2.5 py-[7px] text-[12.5px] transition-colors ${
          isActive
            ? 'bg-primary-50 font-semibold text-primary-800'
            : 'font-medium text-foreground-600 hover:bg-background-100 hover:text-foreground-950'
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-full ${
              isActive ? 'bg-primary-500' : 'opacity-0'
            }`}
          />
          <i className={`${item.icon} text-[15px] ${isActive ? 'text-primary-600' : 'text-foreground-400'}`} />
          <span className="flex-1 truncate">{t(item.labelKey)}</span>
          {item.badge && (
            <span className="rounded-full bg-accent-100 px-1.5 py-0.5 text-[10px] font-semibold text-accent-800">
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
}

function PartnerCard() {
  const { t } = useTranslation();
  const { me } = useAuth();
  const partner = me?.partner;
  return (
    <div className="rounded-md border border-background-200 bg-background-100 p-2.5">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-primary-500 text-[10px] font-semibold text-background-50">
          {initials(partner?.name ?? '')}
        </span>
        <div className="min-w-0">
          <p className="truncate text-[11.5px] font-semibold leading-4 text-foreground-950">{partner?.name}</p>
          <p className="truncate text-[10px] leading-3 text-foreground-500">{partner?.city ? `${partner.city}, UAE` : 'UAE'}</p>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[10px] font-medium text-primary-700">
        <i className="ri-verified-badge-line text-[11px]" />
        {t('portal.shell.contractActive')}
      </div>
    </div>
  );
}

export default function PortalShell() {
  const { t } = useTranslation();
  const location = useLocation();
  const { me, flags, logout } = useAuth();
  const [drawer, setDrawer] = useState(false);
  const [more, setMore] = useState(false);

  useEffect(() => {
    setDrawer(false);
    setMore(false);
  }, [location.pathname]);

  const pageKey = findPortalNavKey(location.pathname);
  const portalNav = visiblePortalNav(flags);
  const tabs = portalNav.filter((item) => mobileTabPaths.includes(item.path));
  const moreItems = portalNav.filter((item) => !mobileTabPaths.includes(item.path));
  const userName = me?.name ?? '';
  const userRole = me ? t(`portal.team.role${me.role}`, { defaultValue: me.role }) : '';
  const partner = me?.partner;

  return (
    <div className="min-h-screen bg-background-100 text-foreground-900">
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-[240px] flex-col border-r border-background-200 bg-background-50 lg:flex">
        <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-background-200 px-4">
          <Link to="/portal" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-plant-line text-[17px]" />
            </span>
            <span className="leading-tight">
              <span className="block text-[13px] font-semibold tracking-tight text-foreground-950">Treejar</span>
              <span className="block text-[9.5px] font-semibold uppercase tracking-[0.14em] text-foreground-400">
                {t('portal.brand')}
              </span>
            </span>
          </Link>
        </div>

        <div className="px-2.5 pt-3">
          <PartnerCard />
        </div>

        <div className="flex-1 overflow-y-auto px-2.5 py-4 thin-scroll">
          <p className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-foreground-400">
            {t('portal.nav.section')}
          </p>
          <nav className="flex flex-col gap-0.5">
            {portalNav.map((item) => (
              <NavRow key={item.path} item={item} />
            ))}
          </nav>
        </div>

        <div className="border-t border-background-200 p-3">
          <Link
            to="/"
            className="mb-2 flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] font-medium text-foreground-500 transition-colors hover:text-primary-700"
          >
            <i className="ri-arrow-left-up-line text-[12px]" />
            {t('portal.hubLink')}
          </Link>
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-200 text-[11px] font-semibold text-secondary-900">
              {initials(userName)}
            </span>
            <div className="min-w-0 flex-1 leading-tight">
              <p className="truncate text-[12px] font-semibold text-foreground-950">{userName}</p>
              <p className="truncate text-[10px] text-foreground-500">{userRole}</p>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              aria-label={t('portal.nav.signOut')}
              title={t('portal.nav.signOut')}
              className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-800"
            >
              <i className="ri-logout-box-r-line text-[14px]" />
            </button>
          </div>
        </div>
      </aside>

      {drawer && (
        <>
          <div
            className="fixed inset-0 z-40 bg-foreground-950/30 lg:hidden"
            onClick={() => setDrawer(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 left-0 z-50 flex w-[268px] flex-col bg-background-50 lg:hidden">
            <div className="flex h-14 items-center justify-between border-b border-background-200 px-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-plant-line text-[17px]" />
                </span>
                <span className="leading-tight">
                  <span className="block text-[13px] font-semibold text-foreground-950">Treejar</span>
                  <span className="block text-[9.5px] font-semibold uppercase tracking-[0.14em] text-foreground-400">
                    {t('portal.brand')}
                  </span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setDrawer(false)}
                aria-label={t('portal.shell.closeNav')}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded text-foreground-500"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="px-2.5 pt-3">
              <PartnerCard />
            </div>
            <nav className="flex-1 overflow-y-auto px-2.5 py-4 thin-scroll">
              <div className="flex flex-col gap-0.5">
                {portalNav.map((item) => (
                  <NavRow key={item.path} item={item} onNavigate={() => setDrawer(false)} />
                ))}
              </div>
            </nav>
            <div className="border-t border-background-200 p-3">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary-200 text-[11px] font-semibold text-secondary-900">
                  {initials(userName)}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[12px] font-semibold text-foreground-950">{userName}</p>
                  <p className="truncate text-[10px] text-foreground-500">{userRole}</p>
                </div>
                <button
                  type="button"
                  onClick={() => void logout()}
                  aria-label={t('portal.nav.signOut')}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded text-foreground-400 hover:text-foreground-800"
                >
                  <i className="ri-logout-box-r-line text-[14px]" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex min-h-screen flex-col lg:pl-[240px]">
        <header className="sticky top-0 z-20 flex h-14 items-center justify-between gap-3 border-b border-background-200 bg-background-50/95 px-4 backdrop-blur md:px-6">
          <div className="flex min-w-0 items-center gap-2.5">
            <button
              type="button"
              aria-label={t('portal.shell.openNav')}
              onClick={() => setDrawer(true)}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-200 text-foreground-600 lg:hidden"
            >
              <i className="ri-menu-line text-[16px]" />
            </button>
            <div className="flex min-w-0 items-center gap-2 text-[12px]">
              <span className="hidden text-foreground-400 sm:inline">{t('portal.brand')}</span>
              <i className="ri-arrow-right-s-line hidden text-[14px] text-foreground-300 sm:inline" />
              <span className="truncate font-semibold text-foreground-950">{t(pageKey)}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <LanguageSwitcher />
            <NotificationBell />
            <div className="hidden h-6 w-px bg-background-200 sm:block" />
            <div className="flex items-center gap-2">
              <div className="hidden text-right leading-tight sm:block">
                <p className="max-w-[190px] truncate text-[11.5px] font-semibold text-foreground-950">
                  {partner?.name}
                </p>
                <p className="max-w-[190px] truncate text-[10px] text-foreground-500">
                  {[partner?.warehouse, partner?.city].filter(Boolean).join(', ')}
                </p>
              </div>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-[11px] font-semibold text-primary-800">
                {initials(userName)}
              </span>
            </div>
          </div>
        </header>

        <main className="flex-1 px-4 pb-24 pt-5 md:px-6 md:pt-6 lg:pb-8">
          <div className="fade-in">
            <Outlet />
          </div>
        </main>

        <footer className="hidden flex-col items-start justify-between gap-1 border-t border-background-200 px-6 py-3 text-[11px] text-foreground-400 sm:flex-row sm:items-center lg:flex">
          <p>
            {t('portal.brand')} · Treejar Trading LLC · Al Quoz Industrial 4, Dubai
          </p>
          <p className="tnum">{t('portal.supplierLine')}</p>
        </footer>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-30 flex h-[58px] items-stretch border-t border-background-200 bg-background-50/95 backdrop-blur lg:hidden">
        {tabs.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/portal'}
            className={({ isActive }) =>
              `relative flex flex-1 flex-col items-center justify-center gap-0.5 ${
                isActive ? 'text-primary-700' : 'text-foreground-500'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className="relative">
                  <i className={`${item.icon} text-[19px]`} />
                  {item.badge && (
                    <span className="absolute -right-2 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-accent-500 px-1 text-[8.5px] font-bold text-background-50">
                      {item.badge}
                    </span>
                  )}
                </span>
                <span className={`text-[9.5px] ${isActive ? 'font-semibold' : 'font-medium'}`}>{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={() => setMore(true)}
          aria-label={t('portal.nav.more')}
          className={`flex flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 ${
            more ? 'text-primary-700' : 'text-foreground-500'
          }`}
        >
          <i className="ri-more-2-line text-[19px]" />
          <span className="text-[9.5px] font-medium">{t('portal.nav.more')}</span>
        </button>
      </nav>

      <FeedbackWidget screen={t(pageKey)} listPath="/portal/feedback" />

      {more && (
        <div className="fixed inset-0 z-50 flex items-end bg-foreground-950/30 lg:hidden" onClick={() => setMore(false)}>
          <div
            className="w-full rounded-t-xl border-t border-background-200 bg-background-50 pb-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-background-200 px-4 py-3">
              <p className="text-[13px] font-semibold text-foreground-950">{t('portal.nav.menu')}</p>
              <button
                type="button"
                onClick={() => setMore(false)}
                aria-label={t('portal.shell.closeNav')}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-foreground-500"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-1 p-3">
              {moreItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2.5 text-[12px] font-medium text-foreground-700"
                >
                  <i className={`${item.icon} text-[15px] text-foreground-400`} />
                  <span className="flex-1 truncate">{t(item.labelKey)}</span>
                  {item.badge && (
                    <span className="rounded-full bg-accent-100 px-1.5 text-[10px] font-semibold text-accent-800">
                      {item.badge}
                    </span>
                  )}
                </Link>
              ))}
            </div>
            <div className="px-3">
              <Link
                to="/"
                className="flex items-center gap-2 rounded-md bg-background-100 px-3 py-2.5 text-[12px] font-medium text-foreground-700"
              >
                <i className="ri-arrow-left-up-line text-[15px] text-foreground-400" />
                {t('portal.hubLink')}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}